// src/components/Ads/FeaturedAdsSection.jsx
// Literal markup match of the reference's .a-ads-section/.a-ad-featured/
// .a-ad-stack(.s1/.s2). Rendered inside HomeTemplate's
// <section class="a-section a-section--sponsors" id="sponsors">. Maps
// directly to the real `advertisements` table (title, description, image,
// website_url, is_active) via GET /ads. First active ad is shown large
// ("featured", matching the .s1/.s2 slot order in the reference); the next
// two stack beside it. This is a simple client-side convention (no
// dedicated "featured" flag exists on the table).
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchActiveAds, fetchHomepageSponsorProducts } from '../../api';
import { resolveImageUrl } from '../../utils/imageUrl';
import './FeaturedAdsSection.css';

// Matches Advertisement::FONT_FAMILIES (guhso-backend/app/Models/Advertisement.php)
// and the weight lists the old blanket CSS @import used to pull for all 11
// families on every homepage load. Loaded on demand per-family below instead,
// so a page with no custom-font ads fetches zero extra typefaces.
const AD_FONT_WEIGHTS = {
  Archivo: '700;900',
  'Bebas Neue': null,
  'DM Sans': '700',
  Figtree: '700;900',
  Inter: '700;900',
  'League Spartan': '700;900',
  Montserrat: '700;900',
  Oswald: '700',
  'Playfair Display': '700;900',
  Poppins: '700;900',
  'Space Grotesk': '700',
};

const loadedAdFonts = new Set();

const ensureAdFontLoaded = (family) => {
  if (!family || loadedAdFonts.has(family) || !(family in AD_FONT_WEIGHTS)) return;
  loadedAdFonts.add(family);
  const weights = AD_FONT_WEIGHTS[family];
  const query = weights ? `:wght@${weights}` : '';
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family).replace(/%20/g, '+')}${query}&display=swap`;
  document.head.appendChild(link);
};

// Temporary visual content requested for the empty local advertisements
// table. Real active ads still take precedence as soon as the API returns
// them, so this fallback does not alter the backend contract.
const DEMO_SPONSORS = [
  {
    id: 'demo-kingston-coffee',
    title: 'Kingston Coffee Co.',
    description: 'Small-batch Blue Mountain roasts, shipped island-wide. Guhso listeners get 15% off their first bag.',
    image: null,
    website_url: null,
    source: 'demo',
    is_demo: true,
  },
  {
    id: 'demo-isle-fitness',
    title: 'Isle Fitness Studio',
    description: 'First class free for Guhso listeners.',
    image: null,
    website_url: null,
    source: 'demo',
    is_demo: true,
  },
  {
    id: 'demo-yaad-tech',
    title: 'Yaad Tech Repairs',
    description: 'Same-day phone and laptop fixes in Kingston.',
    image: null,
    website_url: null,
    source: 'demo',
    is_demo: true,
  },
];

const FeaturedAdsSection = () => {
  const navigate = useNavigate();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    Promise.allSettled([
      fetchActiveAds(),
      fetchHomepageSponsorProducts(),
    ])
      .then(([adsResult, productsResult]) => {
        if (!isMounted) return;

        if (adsResult.status === 'rejected') {
          console.error('Failed to load sponsor ads:', adsResult.reason);
        }
        if (productsResult.status === 'rejected') {
          console.error('Failed to load homepage sponsor products:', productsResult.reason);
        }

        const adsData = adsResult.status === 'fulfilled' ? adsResult.value : [];
        const activeAds = Array.isArray(adsData?.data)
          ? [...adsData.data]
          : Array.isArray(adsData)
            ? [...adsData]
            : [];

        const productData = productsResult.status === 'fulfilled'
          ? productsResult.value
          : {};
        const featuredProduct = productData?.featuredPartner || null;
        const sponsoredProducts = Array.isArray(productData?.sponsored)
          ? productData.sponsored
          : [];

        const explicitFeaturedIndex = activeAds.findIndex((ad) => ad.homepage_placement === 'featured');
        const explicitFeatured = explicitFeaturedIndex >= 0
          ? activeAds.splice(explicitFeaturedIndex, 1)[0]
          : null;
        const explicitSponsored = activeAds.filter((ad) => ad.homepage_placement === 'sponsored');
        const generalAds = activeAds.filter((ad) => !ad.homepage_placement);

        const featured = explicitFeatured || featuredProduct || generalAds.shift() || DEMO_SPONSORS[0];
        const stacked = [...explicitSponsored, ...sponsoredProducts];

        while (stacked.length < 2 && generalAds.length > 0) {
          stacked.push(generalAds.shift());
        }

        for (const demo of DEMO_SPONSORS.slice(1)) {
          if (stacked.length >= 2) break;
          stacked.push(demo);
        }

        const nextAds = [featured, ...stacked.slice(0, 2)];
        nextAds.forEach((ad) => ensureAdFontLoaded(ad?.font_family));
        setAds(nextAds);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const [featured, ...rest] = ads;
  const stacked = rest.slice(0, 2);

  const adDestination = (ad) => {
    if (ad.website_url) return ad.website_url;
    if (ad.phone) return `tel:${String(ad.phone).replace(/[^0-9+]/g, '')}`;
    if (ad.email) return `mailto:${ad.email}`;
    return null;
  };

  const defaultCta = (ad) => ad.website_url ? 'Learn more →' : ad.phone ? 'Call now' : ad.email ? 'Email us' : null;

  const handleAdClick = (ad) => {
    if (ad.source === 'product' && ad.website_url) {
      navigate(ad.website_url);
      return;
    }

    const destination = adDestination(ad);
    if (destination) window.open(destination, '_blank', 'noopener,noreferrer');
  };

  // Demo/filler cards (and any real ad missing every contact field) have
  // nowhere to go — don't render them as clickable/focusable so keyboard
  // and screen-reader users aren't told there's a link that does nothing.
  const isInteractive = (ad) => Boolean(adDestination(ad)) || (ad.source === 'product' && Boolean(ad.website_url));

  const handleAdKeyDown = (event, ad) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    handleAdClick(ad);
  };

  const creativeStyle = (ad) => {
    const image = ad.image ? resolveImageUrl(ad.image) : null;
    const mobileImage = ad.mobile_image ? resolveImageUrl(ad.mobile_image) : null;
    const opacity = Number.isFinite(Number(ad.overlay_opacity))
      ? Math.min(0.9, Math.max(0, Number(ad.overlay_opacity) / 100))
      : 0.45;
    const settings = ad.creative_settings || {};

    return {
      ...(image ? { '--ad-image': `url("${image}")` } : {}),
      ...(mobileImage ? { '--ad-mobile-image': `url("${mobileImage}")` } : {}),
      '--ad-overlay': opacity,
      '--ad-image-x': `${ad.image_position_x ?? 50}%`,
      '--ad-image-y': `${ad.image_position_y ?? 50}%`,
      '--ad-image-zoom': Math.min(2.5, Math.max(1, Number(settings.image_zoom || 100) / 100)),
      '--ad-headline-color': ad.headline_color || '#ffffff',
      '--ad-accent-color': 'var(--color-accent)',
      '--ad-background': settings.background_color || '#1f1004',
      '--ad-gradient': settings.gradient_color || 'color-mix(in srgb, var(--color-accent) 28%, var(--color-surface))',
      '--ad-gradient-angle': `${settings.gradient_angle ?? 140}deg`,
      '--ad-headline-size': `${settings.headline_size ?? 52}px`,
      '--ad-headline-weight': settings.headline_weight ?? 900,
      '--ad-headline-line-height': settings.headline_line_height ?? 0.96,
      '--ad-headline-spacing': `${settings.headline_letter_spacing ?? -1.5}px`,
      ...(ad.font_family ? { '--ad-font-family': `"${ad.font_family}", var(--font-display)` } : {}),
    };
  };

  const textAlignment = (ad) => ad.creative_settings?.text_align || 'left';

  if (loading) {
    return (
      <>
        <div className="a-rail-label">
          <h3>From Our Sponsors</h3>
        </div>
        <div className="a-ads-section">
          <div className="a-ad-featured a-ad-skeleton" />
          <div className="a-ad-stack">
            <div className="a-ad-small a-ad-skeleton" />
            <div className="a-ad-small a-ad-skeleton" />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="a-rail-label">
        <h3>From Our Sponsors</h3>
        <Link to="/directory">Directory →</Link>
      </div>
      <div className="a-ads-section">
        {featured && (
          <div
            className={`a-ad-featured${isInteractive(featured) ? '' : ' is-static'}`}
            style={creativeStyle(featured)}
            data-text-align={textAlignment(featured)}
            onClick={isInteractive(featured) ? () => handleAdClick(featured) : undefined}
            onKeyDown={isInteractive(featured) ? (event) => handleAdKeyDown(event, featured) : undefined}
            role={isInteractive(featured) ? 'link' : undefined}
            tabIndex={isInteractive(featured) ? 0 : undefined}
            aria-label={featured.is_demo ? `${featured.title}, demo sponsor card` : undefined}
          >
            <span className="a-ad-artwork" aria-hidden="true" />
            <span className="a-ad-badge">Featured Partner</span>
            {featured.logo_image && <img className="a-ad-logo" src={resolveImageUrl(featured.logo_image)} alt="" />}
            <h4>{featured.title}</h4>
            {featured.description && <p>{featured.description}</p>}
            {isInteractive(featured) && <span className="go">{featured.cta_text || defaultCta(featured)}</span>}
            <span className="a-ad-sponsor-tag">Sponsored by {featured.title}</span>
          </div>
        )}
        {stacked.length > 0 && (
          <div className="a-ad-stack">
            {stacked.map((ad, i) => (
              <div
                className={`a-ad-small s${i + 1}${isInteractive(ad) ? '' : ' is-static'}`}
                key={ad.id}
                style={creativeStyle(ad)}
                data-text-align={textAlignment(ad)}
                onClick={isInteractive(ad) ? () => handleAdClick(ad) : undefined}
                onKeyDown={isInteractive(ad) ? (event) => handleAdKeyDown(event, ad) : undefined}
                role={isInteractive(ad) ? 'link' : undefined}
                tabIndex={isInteractive(ad) ? 0 : undefined}
                aria-label={ad.is_demo ? `${ad.title}, demo sponsor card` : undefined}
              >
                <span className="a-ad-artwork" aria-hidden="true" />
                <span className="a-ad-badge">Sponsored</span>
                {ad.logo_image && <img className="a-ad-logo" src={resolveImageUrl(ad.logo_image)} alt="" />}
                <h5>{ad.title}</h5>
                {ad.description && <p>{ad.description}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default FeaturedAdsSection;
